import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { PrimaryButton, Modal, Field, TextInput, Select, Textarea, useFetch } from "../../components/crm/ui";
import TicketDetailModal from "../../components/crm/TicketDetailModal";
import { CustomerPicker } from "../../components/crm/CustomerPicker";
import {
  Plus, Search, X, Filter, ChevronLeft, ChevronRight, AlertTriangle, ShieldCheck,
  Wrench, ExternalLink, Clock, ChevronDown, ChevronUp, Download,
} from "lucide-react";

const TYPES = [
  { value: "new_installation", label: "New Installation" },
  { value: "amc", label: "AMC" },
  { value: "repair_maintenance", label: "Repair & Maintenance" },
  { value: "operational_issue", label: "Operational Issue (SC Dispute)" },
];
const STATUSES = ["open", "in_progress", "on_hold", "resolution_support_required", "completed", "closed"];
const PRIORITIES = ["low", "medium", "high", "critical"];

const EMPTY = {
  title: "", description: "", customer_id: "", technician_id: "", service_incharge_id: "",
  type: "repair_maintenance", priority: "medium", material_id: "", serial_code: "",
  sla_hours: 48,
};

const TYPE_LABEL = { new_installation: "New Install", amc: "AMC", repair_maintenance: "Repair" };
const TYPE_TONE = {
  new_installation:   { bg: "bg-emerald-100", fg: "text-emerald-800", dot: "bg-emerald-500" },
  amc:                { bg: "bg-sky-100",     fg: "text-sky-800",     dot: "bg-sky-500" },
  repair_maintenance: { bg: "bg-orange-100",  fg: "text-orange-800",  dot: "bg-orange-500" },
};
const STATUS_TONE = {
  open:                         { bg: "bg-sky-100",     fg: "text-sky-800",     dot: "bg-sky-500" },
  in_progress:                  { bg: "bg-amber-100",   fg: "text-amber-800",   dot: "bg-amber-500" },
  on_hold:                      { bg: "bg-slate-100",   fg: "text-slate-700",   dot: "bg-slate-500" },
  resolution_support_required:  { bg: "bg-rose-100",    fg: "text-rose-800",    dot: "bg-rose-500" },
  completed:                    { bg: "bg-emerald-100", fg: "text-emerald-800", dot: "bg-emerald-500" },
  closed:                       { bg: "bg-emerald-200", fg: "text-emerald-900", dot: "bg-emerald-600" },
};
const PRIO_TONE = {
  low:      { bg: "bg-slate-100",  fg: "text-slate-700",  dot: "bg-slate-500" },
  medium:   { bg: "bg-sky-100",    fg: "text-sky-800",    dot: "bg-sky-500" },
  high:     { bg: "bg-amber-100",  fg: "text-amber-800",  dot: "bg-amber-500" },
  critical: { bg: "bg-red-100",    fg: "text-red-800",    dot: "bg-red-500" },
};

function Chip({ tone, children, testid }) {
  const t = tone || { bg: "bg-slate-100", fg: "text-slate-700", dot: "bg-slate-500" };
  return (
    <span data-testid={testid} className={"inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider " + t.bg + " " + t.fg}>
      <span className={"w-1.5 h-1.5 rounded-full " + t.dot} />
      {children}
    </span>
  );
}

function daysAgo(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 86400000);
    return diff === 0 ? "today" : `${diff}d`;
  } catch { return "—"; }
}

export default function ServiceTickets() {
  const { data: tickets, reload } = useFetch("/service-tickets");
  const { data: customers } = useFetch("/customers");
  const { data: technicians } = useFetch("/technicians");
  const { data: materials } = useFetch("/materials");
  const { data: serviceTeam } = useFetch("/service-team/search");

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detailTicketId, setDetailTicketId] = useState(null);

  // ── Filters ─────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [fType, setFType] = useState([]);
  const [fStatus, setFStatus] = useState([]);
  const [fPriority, setFPriority] = useState([]);
  const [fCustomer, setFCustomer] = useState("");
  const [fOppRef, setFOppRef] = useState("");
  const [fSi, setFSi] = useState("");
  const [fTech, setFTech] = useState("");
  const [fBreached, setFBreached] = useState(false);

  // Drill-down from the CRM Dashboard: seed filters from ?status= / ?breached=true once on mount.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qStatus = params.get("status");
    const qBreached = params.get("breached");
    if (qStatus && STATUSES.includes(qStatus)) { setFStatus([qStatus]); setFilterOpen(true); }
    if (qBreached === "true") { setFBreached(true); setFilterOpen(true); }
  }, []);

  // ── Pagination ─────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const activeFilters = useMemo(() => {
    return [
      ...fType, ...fStatus, ...fPriority,
      fCustomer, fOppRef, fSi ? "si" : null, fTech ? "tech" : null,
      fBreached ? "breach" : null,
    ].filter(Boolean).length;
  }, [fType, fStatus, fPriority, fCustomer, fOppRef, fSi, fTech, fBreached]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tickets.filter((t) => {
      if (fType.length && !fType.includes(t.type)) return false;
      if (fStatus.length && !fStatus.includes(t.status)) return false;
      if (fPriority.length && !fPriority.includes(t.priority)) return false;
      if (fSi && t.service_incharge_id !== fSi) return false;
      if (fTech && t.technician_id !== fTech) return false;
      if (fBreached && !t.sla_breached) return false;
      const c = customers.find((c) => c.id === t.customer_id);
      if (fCustomer) {
        const cq = fCustomer.trim().toLowerCase();
        if (!(c?.customer_name || "").toLowerCase().includes(cq)) return false;
      }
      if (fOppRef) {
        const oq = fOppRef.trim().toLowerCase();
        if (!(t.opportunity_code || "").toLowerCase().includes(oq)) return false;
      }
      if (q) {
        const te = technicians.find((x) => x.id === t.technician_id);
        const hay = [
          t.ticket_number, t.title, t.description, t.opportunity_code, t.opportunity_name,
          c?.customer_name, te?.name, t.serial_code,
        ].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tickets, search, fType, fStatus, fPriority, fCustomer, fOppRef, fSi, fTech, fBreached, customers, technicians]);

  useEffect(() => { setPage(1); }, [search, fType, fStatus, fPriority, fCustomer, fOppRef, fSi, fTech, fBreached, perPage]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageRows = useMemo(() => {
    const start = (page - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, page, perPage]);

  const stats = useMemo(() => ({
    total: tickets.length,
    open: tickets.filter((t) => t.status === "open" || t.status === "in_progress").length,
    breached: tickets.filter((t) => t.sla_breached && !["completed", "closed"].includes(t.status)).length,
    installs: tickets.filter((t) => t.type === "new_installation").length,
  }), [tickets]);

  function startCreate() { setForm(EMPTY); setSelectedCustomer(null); setOpen(true); }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, sla_hours: Number(form.sla_hours) };
    if (!payload.technician_id) delete payload.technician_id;
    if (!payload.service_incharge_id) delete payload.service_incharge_id;
    if (form.type !== "new_installation") { delete payload.material_id; delete payload.serial_code; }
    if (!payload.material_id) delete payload.material_id;
    if (!payload.serial_code) delete payload.serial_code;
    try {
      await api.post("/service-tickets", payload);
      toast.success("Ticket created");
      setOpen(false); reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  }

  async function changeStatus(t, status) {
    try {
      await api.put(`/service-tickets/${t.id}/status?status=${status}`);
      toast.success(`${t.ticket_number}: ${status}`); reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }

  function clearFilters() {
    setFType([]); setFStatus([]); setFPriority([]);
    setFCustomer(""); setFOppRef(""); setFSi(""); setFTech(""); setFBreached(false);
    setSearch("");
  }

  const toggleArr = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  function csvEscape(v) {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }

  function exportCsv() {
    const headers = [
      "Ticket #", "Type", "Title", "Customer", "Opportunity", "Material", "Serial",
      "Service Incharge", "Technician", "Priority", "Status", "SLA (hrs)", "SLA Breached", "Age (days)", "Created At",
    ];
    const rows = filtered.map((t) => {
      const c = customers.find((c) => c.id === t.customer_id);
      const te = technicians.find((x) => x.id === t.technician_id);
      const si = serviceTeam.find((sm) => sm.user_id === t.service_incharge_id);
      const mat = materials.find((m) => m.id === t.material_id);
      return [
        t.ticket_number, TYPE_LABEL[t.type] || t.type, t.title, c?.customer_name || "",
        t.opportunity_code || "", mat?.code || "", t.serial_code || "",
        si?.label || "", te?.name || "", t.priority, t.status.replace(/_/g, " "),
        t.sla_hours, t.sla_breached ? "Yes" : "No", daysAgo(t.created_at), t.created_at || "",
      ];
    });
    const csv = [headers, ...rows].map((r) => r.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `service-tickets-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} ticket${filtered.length === 1 ? "" : "s"} to CSV`);
  }

  return (
    <div data-testid="tickets-page">
      {/* Sticky header — persists while scrolling */}
      <div className="sticky top-[42px] z-30 -mx-6 px-6 py-2.5 bg-white/95 backdrop-blur border-b border-[#DDE5D8]" data-testid="tickets-sticky-header">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-4">
            <div>
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#4A5568]">Field Operations</div>
              <h1 className="font-display text-[22px] font-bold tracking-tight leading-none mt-1">Service Tickets</h1>
            </div>
            {/* KPI strip */}
            <div className="flex items-center gap-1 border-l border-[#DDE5D8] pl-4">
              <KpiChip label="Total" value={stats.total} />
              <KpiChip label="Active" value={stats.open} tone="bg-sky-50 text-sky-800" />
              <KpiChip label="Breached" value={stats.breached} tone="bg-rose-50 text-rose-800" />
              <KpiChip label="Installs" value={stats.installs} tone="bg-emerald-50 text-emerald-800" />
            </div>
          </div>
          <PrimaryButton onClick={startCreate} testid="create-ticket-btn"><Plus className="w-4 h-4" /> New Ticket</PrimaryButton>
        </div>

        {/* Search + filter row */}
        <div className="mt-2 flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2 border border-[#DDE5D8] bg-white px-2 py-1.5">
            <Search className="w-3.5 h-3.5 text-[#94A3B8]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search #, title, customer, opp code, serial, technician…"
              data-testid="tickets-search"
              className="flex-1 text-[12.5px] font-mono outline-none bg-transparent"
            />
            {search && <button onClick={() => setSearch("")}><X className="w-3.5 h-3.5 text-[#94A3B8] hover:text-[#0F1A12]" /></button>}
          </div>
          <button
            onClick={exportCsv}
            disabled={filtered.length === 0}
            data-testid="tickets-export-csv"
            title="Export the currently filtered tickets to CSV"
            className="px-2.5 py-1.5 border border-[#DDE5D8] bg-white text-[#0F1A12] font-mono text-[10.5px] uppercase tracking-widest inline-flex items-center gap-1.5 hover:border-[#1F4E2C] disabled:opacity-40"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <button
            onClick={() => setFilterOpen((v) => !v)}
            data-testid="tickets-filter-toggle"
            className={"px-2.5 py-1.5 border font-mono text-[10.5px] uppercase tracking-widest inline-flex items-center gap-1.5 " + (filterOpen ? "bg-[#1F4E2C] text-white border-[#1F4E2C]" : "bg-white text-[#0F1A12] border-[#DDE5D8] hover:border-[#1F4E2C]")}
          >
            <Filter className="w-3.5 h-3.5" /> Filters{activeFilters > 0 && ` · ${activeFilters}`}
            {filterOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {activeFilters > 0 && (
            <button onClick={clearFilters} data-testid="tickets-filter-clear" className="text-[10.5px] font-mono uppercase tracking-widest text-[#475569] hover:text-[#0F1A12] px-2">Reset</button>
          )}
          <div className="font-mono text-[10.5px] text-[#475569]">
            <span className="text-[#0F1A12] font-semibold">{filtered.length}</span> / {tickets.length}
          </div>
        </div>

        {filterOpen && (
          <div className="mt-2 border border-[#DDE5D8] bg-[#FFFDF8] p-3 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="tickets-filter-panel">
            <FilterMultiSelect label="Type" values={fType} options={TYPES.map(t => ({ value: t.value, label: t.label }))} onToggle={(v) => toggleArr(fType, setFType, v)} testidBase="ff-type" />
            <FilterMultiSelect label="Status" values={fStatus} options={STATUSES.map(s => ({ value: s, label: s.replace(/_/g, " ") }))} onToggle={(v) => toggleArr(fStatus, setFStatus, v)} testidBase="ff-status" />
            <FilterMultiSelect label="Priority" values={fPriority} options={PRIORITIES.map(p => ({ value: p, label: p }))} onToggle={(v) => toggleArr(fPriority, setFPriority, v)} testidBase="ff-prio" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-1">Customer</div>
              <input value={fCustomer} onChange={(e) => setFCustomer(e.target.value)} data-testid="ff-customer" placeholder="customer name…" className="w-full border border-[#DDE5D8] px-2 py-1 text-[12px] font-mono" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-1">Opportunity Ref</div>
              <input value={fOppRef} onChange={(e) => setFOppRef(e.target.value)} data-testid="ff-opp" placeholder="OPP-2026-…" className="w-full border border-[#DDE5D8] px-2 py-1 text-[12px] font-mono" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-1">Service Incharge</div>
              <select value={fSi} onChange={(e) => setFSi(e.target.value)} data-testid="ff-si" className="w-full border border-[#DDE5D8] px-2 py-1 text-[12px] font-mono">
                <option value="">— Any —</option>
                {serviceTeam.map((sm) => (<option key={sm.user_id} value={sm.user_id}>{sm.label}</option>))}
              </select>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-1">Technician</div>
              <select value={fTech} onChange={(e) => setFTech(e.target.value)} data-testid="ff-tech" className="w-full border border-[#DDE5D8] px-2 py-1 text-[12px] font-mono">
                <option value="">— Any —</option>
                {technicians.map((te) => (<option key={te.id} value={te.id}>{te.name}</option>))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-[12px] mt-4 col-span-2 md:col-span-1">
              <input type="checkbox" checked={fBreached} onChange={(e) => setFBreached(e.target.checked)} data-testid="ff-breached" />
              <span className="font-mono uppercase tracking-widest text-[10px] text-[#475569]">SLA Breached only</span>
            </label>
          </div>
        )}
      </div>

      {/* Ticket table */}
      <div className="mt-3 border border-[#DDE5D8] bg-white overflow-x-auto">
        <table className="w-full text-[12px]" data-testid="tickets-table">
          <thead>
            <tr className="text-left font-mono text-[9.5px] uppercase tracking-[0.2em] text-[#475569] bg-[#F4F6F1] border-b border-[#DDE5D8]">
              <th className="px-2.5 py-2 whitespace-nowrap">#</th>
              <th className="px-2.5 py-2">Type</th>
              <th className="px-2.5 py-2">Title / Customer</th>
              <th className="px-2.5 py-2">Opportunity</th>
              <th className="px-2.5 py-2">Material · Serial</th>
              <th className="px-2.5 py-2">Service Incharge</th>
              <th className="px-2.5 py-2">Technician</th>
              <th className="px-2.5 py-2">Priority</th>
              <th className="px-2.5 py-2">Status</th>
              <th className="px-2.5 py-2 whitespace-nowrap">SLA</th>
              <th className="px-2.5 py-2 whitespace-nowrap">Age</th>
              <th className="px-2.5 py-2 text-right">Update</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((t) => {
              const c = customers.find((c) => c.id === t.customer_id);
              const te = technicians.find((te) => te.id === t.technician_id);
              const si = serviceTeam.find((sm) => sm.user_id === t.service_incharge_id);
              const mat = materials.find((m) => m.id === t.material_id);
              return (
                <tr
                  key={t.id}
                  data-testid={`ticket-row-${t.id}`}
                  className={"border-b border-[#EEF2EA] hover:bg-[#F4F6F1] cursor-pointer align-top " + (t.sla_breached ? "bg-rose-50/40" : "")}
                  onClick={() => setDetailTicketId(t.id)}
                >
                  <td className="px-2.5 py-2 font-mono text-[11px] font-semibold text-[#1F4E2C] whitespace-nowrap">{t.ticket_number}</td>
                  <td className="px-2.5 py-2"><Chip tone={TYPE_TONE[t.type]}>{TYPE_LABEL[t.type] || t.type}</Chip></td>
                  <td className="px-2.5 py-2 max-w-[260px]">
                    <div className="font-medium text-[#0F1A12] truncate" title={t.title}>{t.title}</div>
                    <div className="text-[#475569] text-[11px] truncate" title={c?.customer_name}>{c?.customer_name || "—"}</div>
                    <div className="flex gap-1 mt-0.5">
                      {t.is_billable && <span className="text-[9px] font-mono uppercase tracking-wider bg-[#1F4E2C] text-white px-1 py-0.5">Bill</span>}
                      {t.warranty_status === "in_warranty" && <span className="text-[9px] font-mono uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1 py-0.5 inline-flex items-center gap-0.5"><ShieldCheck className="w-2.5 h-2.5" />Warranty</span>}
                      {t.is_serviceable === false && (
                        <span
                          className="text-[9px] font-mono uppercase tracking-wider bg-red-100 text-red-700 px-1 py-0.5 inline-flex items-center gap-0.5"
                          data-testid={`ticket-not-serviceable-${t.id}`}
                          title={t.service_check_reason || ""}
                        >
                          <AlertTriangle className="w-2.5 h-2.5" />Not Serviceable
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2.5 py-2 max-w-[220px]" onClick={(e) => e.stopPropagation()}>
                    {t.opportunity_code ? (
                      <Link to={`/crm/opportunities/${t.linked_opp_id}`} data-testid={`ticket-opp-${t.id}`} className="inline-flex items-center gap-1 text-[11px] font-mono font-semibold text-[#1F4E2C] hover:underline">
                        {t.opportunity_code}<ExternalLink className="w-2.5 h-2.5" />
                      </Link>
                    ) : <span className="text-[#94A3B8] font-mono text-[11px]">—</span>}
                    {t.opportunity_name && (
                      <div className="text-[10.5px] text-[#475569] truncate" title={t.opportunity_name}>{t.opportunity_name}</div>
                    )}
                  </td>
                  <td className="px-2.5 py-2 max-w-[180px]">
                    <div className="text-[11px] font-mono truncate" title={mat?.name}>{mat?.code || "—"}</div>
                    <div className="text-[10.5px] text-[#475569] font-mono truncate" title={t.serial_code}>{t.serial_code || "—"}</div>
                  </td>
                  <td className="px-2.5 py-2 max-w-[160px]">
                    <div className="truncate text-[11.5px]" title={si?.label}>{si?.label || <span className="text-[#94A3B8] italic">—</span>}</div>
                    {si?.type && <div className="text-[10px] text-[#94A3B8] font-mono uppercase">{si.type === "service_franchisee" ? "Franchisee" : "Manager"}</div>}
                  </td>
                  <td className="px-2.5 py-2 max-w-[140px]">
                    <div className="truncate text-[11.5px]" title={te?.name}>{te?.name || <span className="text-red-600 font-mono uppercase text-[10.5px]">Unassigned</span>}</div>
                    {te?.speciality && <div className="text-[10px] text-[#94A3B8] font-mono truncate">{te.speciality}</div>}
                  </td>
                  <td className="px-2.5 py-2"><Chip tone={PRIO_TONE[t.priority]}>{t.priority}</Chip></td>
                  <td className="px-2.5 py-2"><Chip tone={STATUS_TONE[t.status]}>{t.status.replace(/_/g, " ")}</Chip></td>
                  <td className="px-2.5 py-2 whitespace-nowrap">
                    <div className="font-mono text-[10.5px] text-[#475569]">{t.sla_hours}h</div>
                    {t.sla_breached && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] font-mono uppercase tracking-wider bg-red-600 text-white px-1 py-0.5"><AlertTriangle className="w-2.5 h-2.5" /> Breach</span>
                    )}
                  </td>
                  <td className="px-2.5 py-2 whitespace-nowrap font-mono text-[10.5px] text-[#475569]">
                    <Clock className="w-3 h-3 inline mr-0.5 text-[#94A3B8]" />
                    {daysAgo(t.created_at)}
                  </td>
                  <td className="px-2.5 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={t.status}
                      onChange={(e) => changeStatus(t, e.target.value)}
                      data-testid={`ticket-status-${t.id}`}
                      className="text-[10.5px] border border-[#DDE5D8] px-1.5 py-0.5 font-mono uppercase bg-white"
                    >
                      {STATUSES.map((s) => (<option key={s} value={s}>{s.replace(/_/g, " ")}</option>))}
                    </select>
                  </td>
                </tr>
              );
            })}
            {pageRows.length === 0 && (
              <tr><td colSpan={12} className="px-4 py-10 text-center text-[13px] text-[#475569]">
                {tickets.length === 0 ? "No tickets yet." : "No tickets match the filters."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filtered.length > 0 && (
        <div className="mt-2 flex items-center justify-between text-[11px] font-mono" data-testid="tickets-pager">
          <div className="text-[#475569]">
            Showing <span className="font-semibold text-[#0F1A12]">{(page - 1) * perPage + 1}</span>–
            <span className="font-semibold text-[#0F1A12]">{Math.min(page * perPage, filtered.length)}</span> of
            <span className="font-semibold text-[#0F1A12]"> {filtered.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[#475569] uppercase tracking-widest text-[9.5px]">rows</label>
            <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))} data-testid="tickets-per-page" className="border border-[#DDE5D8] px-1.5 py-0.5 text-[11px]">
              {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <button data-testid="tickets-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="p-1 border border-[#DDE5D8] disabled:opacity-30"><ChevronLeft className="w-3.5 h-3.5" /></button>
            <span className="px-2 tabular-nums">{page} / {totalPages}</span>
            <button data-testid="tickets-next" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="p-1 border border-[#DDE5D8] disabled:opacity-30"><ChevronRight className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New Service Ticket" wide>
        <NewTicketForm
          form={form} setForm={setForm} saving={saving} onSubmit={save} onClose={() => setOpen(false)}
          customers={customers} technicians={technicians} materials={materials} serviceTeam={serviceTeam}
          selectedCustomer={selectedCustomer} setSelectedCustomer={setSelectedCustomer}
        />
      </Modal>

      <TicketDetailModal
        open={!!detailTicketId}
        ticketId={detailTicketId}
        onClose={() => setDetailTicketId(null)}
        onChanged={reload}
      />
    </div>
  );
}

function KpiChip({ label, value, tone = "bg-slate-100 text-slate-800" }) {
  return (
    <div className={"px-2 py-1 flex items-center gap-1.5 " + tone}>
      <div className="font-display text-[15px] font-bold leading-none tabular-nums">{value}</div>
      <div className="font-mono text-[9px] uppercase tracking-widest leading-none">{label}</div>
    </div>
  );
}

function FilterMultiSelect({ label, values, options, onToggle, testidBase }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mb-1">{label}</div>
      <div className="flex flex-wrap gap-1">
        {options.map((o) => {
          const active = values.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onToggle(o.value)}
              data-testid={`${testidBase}-${o.value}`}
              className={"px-2 py-0.5 text-[10.5px] font-mono uppercase tracking-wider transition-colors border " + (active ? "bg-[#1F4E2C] text-white border-[#1F4E2C]" : "bg-white text-[#475569] border-[#DDE5D8] hover:border-[#1F4E2C]")}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NewTicketForm({ form, setForm, saving, onSubmit, onClose, technicians, materials, serviceTeam, selectedCustomer, setSelectedCustomer }) {
  return (
    <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Field label="Title *">
        <TextInput required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ticket-title" />
      </Field>
      <Field label="Type *">
        <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="ticket-type">
          {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </Select>
      </Field>
      <Field label="Customer *">
        <CustomerPicker
          selected={selectedCustomer}
          onSelect={(c) => { setSelectedCustomer(c); setForm({ ...form, customer_id: c.id }); }}
          placeholder="Search customer by name, phone or email…"
          testid="ticket-customer"
          required
        />
      </Field>
      <Field label="Assign Technician">
        <Select value={form.technician_id} onChange={(e) => setForm({ ...form, technician_id: e.target.value })} data-testid="ticket-tech">
          <option value="">— Unassigned —</option>
          {technicians.map((te) => <option key={te.id} value={te.id}>{te.name}</option>)}
        </Select>
      </Field>
      <Field label="Service Incharge (Franchisee / Manager)">
        <Select value={form.service_incharge_id} onChange={(e) => setForm({ ...form, service_incharge_id: e.target.value })} data-testid="ticket-si">
          <option value="">— Unassigned —</option>
          {serviceTeam.map((sm) => (
            <option key={sm.user_id} value={sm.user_id}>
              {sm.label} · {sm.type === "service_franchisee" ? "Franchisee" : "Manager"}
            </option>
          ))}
        </Select>
      </Field>
      {selectedCustomer && (
        <div className="md:col-span-2 bg-[#F4F6F1] p-2.5 text-[11px] font-mono grid grid-cols-3 gap-3">
          <div><div className="text-[#475569]">Phone</div>{selectedCustomer.phone}</div>
          <div><div className="text-[#475569]">Email</div>{selectedCustomer.email}</div>
          <div><div className="text-[#475569]">Address</div>{selectedCustomer.address || "—"}</div>
        </div>
      )}
      <Field label="Priority">
        <Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
          {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
        </Select>
      </Field>
      <Field label="SLA (hours)">
        <TextInput type="number" value={form.sla_hours} onChange={(e) => setForm({ ...form, sla_hours: e.target.value })} />
      </Field>
      {(form.type === "new_installation" || form.type === "repair_maintenance") && (
        <>
          <Field label={form.type === "new_installation" ? "Material *" : "Material (if known)"}>
            <Select required={form.type === "new_installation"} value={form.material_id} onChange={(e) => setForm({ ...form, material_id: e.target.value })} data-testid="ticket-material">
              <option value="">— Select —</option>
              {materials.map((m) => <option key={m.id} value={m.id}>{`${m.code} · ${m.name}`}</option>)}
            </Select>
          </Field>
          <Field label={form.type === "new_installation" ? "Serial Code *" : "Serial Code (for warranty check)"}>
            <TextInput required={form.type === "new_installation"} value={form.serial_code} onChange={(e) => setForm({ ...form, serial_code: e.target.value })} data-testid="ticket-serial" />
          </Field>
        </>
      )}
      <Field label="Description" className="md:col-span-2">
        <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </Field>
      <div className="md:col-span-2 flex justify-end gap-3 pt-1">
        <button type="button" onClick={onClose} className="px-3 py-1.5 font-mono text-[10.5px] uppercase tracking-widest text-[#475569]">Cancel</button>
        <PrimaryButton type="submit" disabled={saving} testid="ticket-save-btn"><Wrench className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Create ticket"}</PrimaryButton>
      </div>
    </form>
  );
}
